import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface Language {
  key: string;
  name: string;
}

const LANGUAGE_SELECTOR_ID = "language-selector";

export const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const languages: Language[] = [
    { key: "en", name: "EN" },
    { key: "es", name: "ES" },
  ];
  const [isOpen, setIsOpen] = useState(false);

  const selectedLanguage =
    languages.find((l) => l.key === i18n.language) ||
    languages.find((l) => l.key === "es") ||
    languages[0];

  const handleLanguageChange = async (language: Language) => {
    await i18n.changeLanguage(language.key);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleWindowClick = (event: MouseEvent) => {
      const target = (event.target as HTMLElement).closest("button");
      if (target && target.id === LANGUAGE_SELECTOR_ID) return;
      setIsOpen(false);
    };
    window.addEventListener("click", handleWindowClick);
    return () => window.removeEventListener("click", handleWindowClick);
  }, []);

  return (
    <div className="relative inline-block text-left">
      <button
        onClick={() => setIsOpen(!isOpen)}
        type="button"
        className="inline-flex items-center gap-2 h-10 px-3 swiss-caption text-ink hover:text-accent"
        id={LANGUAGE_SELECTOR_ID}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        {selectedLanguage.name}
        <span aria-hidden="true">/</span>
        <span className="text-ink-50">
          {languages.find((l) => l.key !== selectedLanguage.key)?.name}
        </span>
      </button>
      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-32 bg-paper-pure border border-ink-14 z-50"
          role="menu"
        >
          {languages.map((language) => (
            <button
              key={language.key}
              onClick={() => handleLanguageChange(language)}
              className={`w-full text-left px-4 py-3 swiss-caption transition-colors ${
                selectedLanguage.key === language.key
                  ? "text-ink bg-ink-6"
                  : "text-ink-70 hover:text-ink hover:bg-ink-6"
              }`}
              role="menuitem"
            >
              {language.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
